/**
 * @file Modal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-11
 * @description Reusable modal component with overlay, animations, and flexible sizing
 */

import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './Modal.css';

/**
 * Reusable Modal Component
 *
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onClose - Callback when modal closes
 * @param {string} title - Optional modal title
 * @param {React.Node} children - Modal content
 * @param {string} size - Modal size: 'sm', 'md', 'lg', 'xl' (default: 'md')
 * @param {boolean} showHeader - Whether to show the header (default: true if title provided)
 * @param {React.Node} footer - Optional footer content
 * @param {boolean} closeOnOverlayClick - Close modal when clicking overlay (default: true)
 * @param {boolean} closeOnEscape - Close modal on ESC key (default: true)
 * @param {string} className - Additional CSS class for modal content
 */
function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showHeader = true,
  footer,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  className = ''
}) {
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(isOpen);

  // Handle the closing animation
  const handleClose = useCallback((e) => {
    if (e) e.stopPropagation();
    setIsClosing(true);
    // Wait for animation to complete before calling onClose
    setTimeout(() => {
      setIsClosing(false);
      setShouldRender(false);
      onClose();
    }, 450); // Slightly less than animation duration to ensure smooth transition
  }, [onClose]);

  // Sync shouldRender with isOpen
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
    } else if (shouldRender && !isClosing) {
      // isOpen went false externally — trigger close animation
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
        setShouldRender(false);
      }, 450);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (shouldRender) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [shouldRender]);

  // Handle ESC key to close modal
  useEffect(() => {
    if (!shouldRender || !closeOnEscape || isClosing) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [shouldRender, closeOnEscape, handleClose, isClosing]);

  // Handle overlay click - stop propagation to prevent closing parent modals
  const handleOverlayClick = (e) => {
    e.stopPropagation();
    if (closeOnOverlayClick && e.target === e.currentTarget && !isClosing) {
      handleClose();
    }
  };

  if (!shouldRender) return null;

  // Handle close button click - stop propagation to prevent closing parent modals
  const handleCloseClick = (e) => {
    e.stopPropagation();
    if (!isClosing) handleClose();
  };

  // Handle content click - stop propagation to prevent closing parent modals
  const handleContentClick = (e) => {
    e.stopPropagation();
  };

  return createPortal(
    <div className={`modal-overlay ${isClosing ? 'closing' : ''}`} onClick={handleOverlayClick}>
      <div className={`modal-content modal-${size} ${className} ${isClosing ? 'closing' : ''}`} onClick={handleContentClick}>
        {showHeader && (title || onClose) && (
          <div className="modal-header">
            {title && <h2 className="modal-title">{title}</h2>}
            <button
              className="modal-close"
              onClick={handleCloseClick}
              aria-label="Close modal"
            >
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
        )}

        <div className="modal-body">
          {children}
        </div>

        {footer && (
          <div className="modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default Modal;
